import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

const CUSTOM_DOZEN_VARIANT_ID = window.location.href.includes("georgetown-cupcake-development.myshopify.com") ? "41451295047813" : '47500743114979';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const prevValue = this.refs.quantityInput.value;
    this.refs.quantityInput.stepUp();
    // Update DOM attribute to match property value
    this.refs.quantityInput.setAttribute('value', this.refs.quantityInput.value);
    this.#onQuantityChange(true, prevValue);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    // Update DOM attribute to match property value
    this.refs.quantityInput.setAttribute('value', this.refs.quantityInput.value);
    this.#onQuantityChange(false);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
      // Update DOM attribute to match property value
      this.refs.quantityInput.setAttribute('value', this.refs.quantityInput.value);
    }
    this.#onQuantityChange(true);
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange(validate = false, prevValue = null) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine), quantityInput.dataset.cartFlavor === 'true', quantityInput.dataset.variantId, quantityInput.dataset.inventoryGeorgetown, quantityInput.dataset.inventoryBethesda, quantityInput.dataset.inventoryNationwide, quantityInput.dataset.date, quantityInput.dataset.flavors, quantityInput.dataset.location, validate, prevValue) );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { value: newValue } = quantityInput;
    const dayInput = document.querySelector('.js-delivery-date');
    const locationInput = document.querySelector('.js-location');
    const locationValue = locationInput ? locationInput.value : 'nationwide';
    const dayValue = dayInput ? dayInput.value : '';
    const isFlavor = Boolean(quantityInput.dataset.cartFlavor);
    const today = new Date().toLocaleDateString('en-GB', { 
      day: '2-digit',
      month: 'long', 
      year: 'numeric' 
    });
    const valueNumber = Number(newValue);
    const maxValue = quantityInput.hasAttribute('max') ? Number(quantityInput.getAttribute('max')) : Infinity;

    this.refs.quantityButtons[1].disabled = false;
    this.refs.quantityButtons[1].classList.remove('button--js-disabled');

    if (valueNumber >= maxValue) {
      this.refs.quantityButtons[1].disabled = true;
      this.refs.quantityButtons[1].classList.add('button--js-disabled');
    }
    if (!quantityInput.closest('.quantity-selector--cart')) {
      const needsInventoryCheck = this.shouldCheckInventory(isFlavor, today, dayValue);
      
      if (needsInventoryCheck) {
        const maxInventory = this.getMaxInventoryForLocation(locationValue, quantityInput);
        
        if (valueNumber >= maxInventory) {
          quantityInput.value = String(maxInventory);
          // Update DOM attribute to match property value
          quantityInput.setAttribute('value', String(maxInventory));
  
          if(isFlavor) {
            const popup = document.querySelector('.js-popup-inventory');
            if (popup) {
              popup.classList.remove('hidden');
              popup.querySelector('.js-popup-text').textContent = maxInventory;
            }
          }
          this.refs.quantityButtons[1].disabled = true;
          this.refs.quantityButtons[1].classList.add('button--js-disabled');
        }
      }

      if (valueNumber <= 0 || isNaN(valueNumber)) {
        this.refs.quantityButtons[0].disabled = true;
        this.refs.quantityButtons[0].classList.add('button--js-disabled');
      } else {
        this.refs.quantityButtons[0].disabled = false;
        this.refs.quantityButtons[0].classList.remove('button--js-disabled');
      }

      if (valueNumber > 0) {
        quantityInput.classList.add('is-active');
      } else {
        quantityInput.classList.remove('is-active');
      }
    }
  };

  /**
   * Determines if inventory check is needed based on item type and date
   * @param {boolean} isFlavor - Whether the item is a flavor
   * @param {string} today - Today's date
   * @param {string} dayValue - Selected delivery date
   * @returns {boolean} Whether inventory check is needed
   */
  shouldCheckInventory(isFlavor, today, dayValue) {
    const isMerch = this.refs.quantityInput?.dataset.merch === 'true';
    
    if (isMerch) {
      return true;
    }
    
    return (isFlavor && today === dayValue) 
        || (this.dataset.variantId === CUSTOM_DOZEN_VARIANT_ID && today === dayValue)
        || (!isFlavor && this.dataset.variantId !== CUSTOM_DOZEN_VARIANT_ID && today === dayValue);
  }

  /**
   * Gets maximum inventory for the specified location
   * @param {string} locationValue - The location (georgetown, bethesda, or nationwide)
   * @param {HTMLInputElement} quantityInput - The quantity input element
   * @returns {number} Maximum available inventory
   */
  getMaxInventoryForLocation(locationValue, quantityInput) {
    switch (locationValue) {
      case 'georgetown':
        return +(quantityInput.dataset.inventoryGeorgetown || 0);
      case 'bethesda':
        return +(quantityInput.dataset.inventoryBethesda || 0);
      default:
        return +(quantityInput.dataset.inventoryNationwide || 0);
    }
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
